// Generated from scripts/header-drawer.ts. Rebuild with pnpm scripts:build.
import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { DrawerCloseEvent, DrawerOpenEvent } from '@theme/theme-drawer';
const PANEL_ENTERING = 'menu-drawer__panel--entering';
const PANEL_LEAVING = 'menu-drawer__panel--leaving';
/**
 * The main menu inside the menu theme-drawer.
 *
 * The drawer itself (open, close, focus trap, scroll lock, Escape, backdrop) belongs to
 * `theme-drawer`. This element owns what happens inside it: menu groups push a panel over the
 * root list, the back control pops it, the header trigger reports the open state, and the
 * search field hands over to the live search panel.
 *
 * Refs: `root` is the scrollable root view; every pushed view is a `[data-menu-panel]`.
 */
class HeaderDrawer extends Component {
    requiredRefs = ['root'];
    /** Pushed panels, bottom to top. */
    #stack = [];
    /** The control that pushed each panel, so focus returns to it on back. */
    #openers = new WeakMap();
    #busy = false;
    get #drawer() {
        return this.closest('theme-drawer');
    }
    get #root() {
        return this.refs.root;
    }
    /** The buttons in the header that open this drawer. */
    get #triggers() {
        const id = this.#drawer?.id;
        return id ? Array.from(document.querySelectorAll(`[aria-controls="${id}"]`)) : [];
    }
    connectedCallback() {
        super.connectedCallback();
        const drawer = this.#drawer;
        drawer?.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerOpen);
        drawer?.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerClose);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        const drawer = this.#drawer;
        drawer?.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerOpen);
        drawer?.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerClose);
    }
    #onDrawerOpen = () => {
        for (const trigger of this.#triggers)
            trigger.setAttribute('aria-expanded', 'true');
    };
    #onDrawerClose = () => {
        for (const trigger of this.#triggers)
            trigger.setAttribute('aria-expanded', 'false');
        this.reset();
    };
    /** The panel for a menu group. */
    #panel(id) {
        return this.querySelector(`[data-menu-panel="${CSS.escape(id)}"]`);
    }
    /** The view a pushed panel covers: the previous panel, or the root. */
    #below(panel) {
        const index = this.#stack.indexOf(panel);
        return (index > 0 ? this.#stack[index - 1] : undefined) ?? this.#root;
    }
    /**
     * Pushes a group's panel over the current view.
     *
     * @param id - The panel id, from `data-menu-panel`.
     * @param event - The click on the group's control.
     */
    async openPanel(id, event) {
        const panel = this.#panel(id);
        if (!panel || this.#busy || this.#stack.includes(panel))
            return;
        const opener = event?.target instanceof Element ? event.target.closest('[aria-controls]') : null;
        const below = this.#stack.at(-1) ?? this.#root;
        this.#busy = true;
        if (opener) {
            opener.setAttribute('aria-expanded', 'true');
            this.#openers.set(panel, opener);
        }
        this.#stack.push(panel);
        panel.hidden = false;
        panel.classList.add(PANEL_ENTERING);
        panel.scrollTop = 0;
        // The covered view leaves the tab order while the panel is on top.
        below.inert = true;
        this.#focusStart(panel);
        await onAnimationEnd(panel, undefined, { subtree: false });
        panel.classList.remove(PANEL_ENTERING);
        this.#busy = false;
    }
    /** Pops the topmost panel and returns focus to the control that pushed it. */
    async back() {
        const panel = this.#stack.at(-1);
        if (!panel || this.#busy)
            return;
        this.#busy = true;
        const below = this.#below(panel);
        this.#stack.pop();
        below.inert = false;
        const opener = this.#openers.get(panel);
        opener?.setAttribute('aria-expanded', 'false');
        opener?.focus({ preventScroll: true });
        panel.classList.add(PANEL_LEAVING);
        await onAnimationEnd(panel, undefined, { subtree: false });
        panel.classList.remove(PANEL_LEAVING);
        panel.hidden = true;
        this.#busy = false;
    }
    /** Returns every view to its initial state without motion. */
    reset() {
        for (const panel of this.#stack) {
            panel.classList.remove(PANEL_ENTERING, PANEL_LEAVING);
            panel.hidden = true;
            panel.inert = false;
            this.#openers.get(panel)?.setAttribute('aria-expanded', 'false');
        }
        this.#stack = [];
        this.#root.inert = false;
        this.#root.scrollTop = 0;
        this.#busy = false;
    }
    /**
     * Opens the live search panel in place of the drawer. Without a search dialog on the page the
     * control is a plain link to the search page and keeps its navigation.
     *
     * The drawer starts closing and the panel opens in the same task as the tap. Waiting for the
     * drawer first would move the field's focus out of the tap, and iOS then keeps the keyboard down.
     */
    openSearch(event) {
        const modal = document.querySelector('#search-modal');
        if (!modal || typeof modal.showDialog !== 'function')
            return;
        event.preventDefault();
        void this.#drawer?.close();
        modal.showDialog();
    }
    /** Moves focus to the panel's back control, so the pushed view is announced from its head. */
    #focusStart(panel) {
        panel.querySelector('[data-menu-back]')?.focus({ preventScroll: true });
    }
}
if (!customElements.get('header-drawer')) {
    customElements.define('header-drawer', HeaderDrawer);
}
